import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import CustomSelect from "../../component/CustomSelect";
import DataTable from "../../component/DataTable";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import Icons from "../../myIcon/Icons";
import { StatusData, formatStatus } from "./RbacOptions";

const endpoints = ApiEndpoints.rbac;

const defaultForm = {
  name: "",
  email: "",
  password: "",
  phone: "",
  role_id: "",
  status: "1",
};

function UserModal({ show, onHide, userId, roles, onSaved }) {
  const [form, setForm] = useState(defaultForm);
  const isEdit = Boolean(userId);

  useEffect(() => {
    if (!show) return;
    if (!userId) {
      setForm(defaultForm);
      return;
    }

    ApiRequest("GET", `${endpoints.userEdit}${userId}`)
      .then((response) => setForm({
        name: response.user?.name || "",
        email: response.user?.email || "",
        password: "",
        phone: response.user?.phone || "",
        role_id: response.user?.role_id || "",
        status: String(response.user?.status ?? "1"),
      }))
      .catch(() => toast.error("Unable to fetch user."));
  }, [show, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.role_id || (!isEdit && !form.password.trim())) {
      toast.error("Name, email, role and password are required.");
      return;
    }

    try {
      const endpoint = isEdit ? `${endpoints.userUpdate}${userId}` : endpoints.userCreate;
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;

      const response = await ApiRequest("POST", endpoint, payload);
      if (response.error) {
        toast.error(response.message);
        return;
      }
      toast.success(response.message);
      onSaved();
      onHide();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save user.");
    }
  };

  return (
    <Modal className="add-modal-area" show={show} onHide={onHide} size="md" centered>
      <Modal.Body>
        <button className="modal-close-button" onClick={onHide}><Icons name="cross" /></button>
        <form onSubmit={handleSubmit}>
          <div className="add-page-sections">{isEdit ? "Edit User" : "Create User"}</div>
          <div className="row">
            <div className="col-md-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add"><label>Name<small>*</small></label></div>
                <input name="name" value={form.name} className="form-control" placeholder="Enter name" onChange={handleChange} required />
              </div>
            </div>
            <div className="col-md-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add"><label>Email<small>*</small></label></div>
                <input name="email" type="email" value={form.email} className="form-control" placeholder="Enter email" onChange={handleChange} required />
              </div>
            </div>
            <div className="col-md-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add"><label>Password{!isEdit && <small>*</small>}</label></div>
                <input name="password" type="password" value={form.password} className="form-control" placeholder={isEdit ? "Leave blank to keep same" : "Enter password"} onChange={handleChange} required={!isEdit} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add"><label>Phone</label></div>
                <input name="phone" value={form.phone} className="form-control" placeholder="Enter phone" onChange={handleChange} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add"><label>Role<small>*</small></label></div>
                <CustomSelect name="role_id" options={roles} defaultValue={form.role_id} placeholder="Select Role" onChange={handleChange} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add"><label>Status<small>*</small></label></div>
                <CustomSelect name="status" options={StatusData} defaultValue={form.status} onChange={handleChange} />
              </div>
            </div>
          </div>
          <button type="submit" className="default-btn">save<span></span></button>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default function Users() {
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userId, setUserId] = useState("");
  const [modalShow, setModalShow] = useState(false);

  const dataFetch = async () => {
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        ApiRequest("GET", endpoints.userList),
        ApiRequest("GET", endpoints.roleList),
      ]);
      setRoles(rolesResponse.roles || []);
      setData((usersResponse.users || []).map((item) => ({
        ...item,
        roleText: item.role_name || "N/A",
        statusText: formatStatus(item.status),
      })));
    } catch (error) {
      toast.error("Unable to fetch users.");
    }
  };

  useEffect(() => {
    dataFetch();
  }, []);

  const handleDelete = async (user_id) => {
    if (!window.confirm("Delete this user?")) return;
    const response = await ApiRequest("POST", endpoints.userDelete, { user_id });
    toast.success(response.message);
    dataFetch();
  };

  const openCreate = () => {
    setUserId("");
    setModalShow(true);
  };

  return (
    <>
      <AdminHeader pagename={{ name: "User Management", user: "Admin", innerpage: "User Management / Users" }} />
      <div className="add-btn-section">
        <Link className="admin-add-btn" onClick={openCreate}> Add +</Link>
        <DataTable
          data={data}
          tableHeader={["Date", "Name", "Email", "Role", "Status"]}
          tbody={["createdAt", "name", "email", "roleText", "statusText"]}
          setModalShow={setModalShow}
          setId={setUserId}
          onDelete={handleDelete}
        />
      </div>
      <UserModal show={modalShow} userId={userId} roles={roles} onHide={() => setModalShow(false)} onSaved={dataFetch} />
    </>
  );
}

